/**
 * Loyalty Discount Countdown Sync
 * Synchronizes loyalty discount visibility with announcement bar countdown
 */

class LoyaltyCountdownSync {
  constructor() {
    this.settings = window.theme?.settings || {};
    this.checkInterval = null;
    this.priceElements = null;
    this.cartPriceElements = null;
    this.boundCountdownHandler = this.handleCountdownExpired.bind(this);
    this.cartObserver = null;
    this.init();
  }

  init() {
    // Only run if sync is enabled
    if (!this.settings.loyalty_sync_with_countdown) {
      // console.log('Loyalty sync: Not enabled');
      return;
    }

    // console.log('Loyalty sync: Initializing...');

    // Try to get settings immediately
    this.getCountdownSettings();
    
    // Check campaign status immediately to show/hide announcement bar
    if (this.countdownSettings) {
      const campaignPhase = this.getCampaignPhase();
      this.updateAnnouncementBarVisibility(campaignPhase);
    }
    
    // Set up periodic checking for settings and campaign status
    this.startPeriodicChecking();

    // Listen for countdown events
    this.listenForCountdownEvents();
    
    // Observe cart drawer for changes
    this.observeCartDrawer();
  }

  getCountdownSettings() {
    // Try multiple sources for countdown settings
    const announcementBar = document.querySelector('[data-section-type="announcement-bar"]');
    
    if (!announcementBar) {
      // console.log('Loyalty sync: No announcement bar found');
      return false;
    }

    // First, check if we have countdown settings in window object (from Liquid)
    if (window.countdownSettings) {
      this.countdownSettings = window.countdownSettings;
      // console.log('Loyalty sync: Found countdown settings in window', this.countdownSettings);
      return true;
    }

    // Try to find countdown element with data attributes
    const countdownEl = announcementBar.querySelector('[id^="countdown-"]');
    if (countdownEl) {
      // Check for data attributes on countdown element
      const campaignStart = countdownEl.dataset.campaignStart;
      if (campaignStart) {
        this.countdownSettings = {
          enabled: true,
          startDate: campaignStart,
          endDate: countdownEl.dataset.campaignEnd || '',
          twoPhaseEnabled: countdownEl.dataset.twoPhase === 'true',
          phase1Hours: parseInt(countdownEl.dataset.phase1Hours || 30)
        };
        // console.log('Loyalty sync: Found countdown settings from countdown element', this.countdownSettings);
        return true;
      }
    }

    // Try to find non-member message element which has campaign dates
    const nonMemberEl = announcementBar.querySelector('[data-campaign-start]');
    if (nonMemberEl) {
      this.countdownSettings = {
        enabled: true,
        startDate: nonMemberEl.dataset.campaignStart,
        endDate: '', // Will be extracted from scripts below
        twoPhaseEnabled: true,
        phase1Hours: parseInt(nonMemberEl.dataset.phase1Hours || 30)
      };
      // console.log('Loyalty sync: Found campaign start from non-member element', this.countdownSettings);
    }

    // Extract settings from the inline script in announcement.liquid
    const scripts = announcementBar.querySelectorAll('script');
    let foundSettings = false;
    
    scripts.forEach(script => {
      const content = script.textContent;
      if (content.includes('campaignStartStr') || content.includes('campaignEndStr')) {
        // Try to extract dates from script content
        const campaignStartMatch = content.match(/campaignStartStr\s*=\s*['"]([^'"]+)['"]/);
        const campaignEndMatch = content.match(/campaignEndStr\s*=\s*['"]([^'"]+)['"]/);
        
        if (campaignStartMatch || campaignEndMatch) {
          if (!this.countdownSettings) {
            this.countdownSettings = {
              enabled: true,
              startDate: campaignStartMatch?.[1] || '',
              endDate: campaignEndMatch?.[1] || '',
              twoPhaseEnabled: content.includes('phase1Hours'),
              phase1Hours: parseInt(content.match(/phase1Hours\s*=\s*parseInt\(['"](\d+)['"]/)?.[1] || 30)
            };
          } else {
            // Update existing settings with end date if missing
            if (campaignEndMatch && !this.countdownSettings.endDate) {
              this.countdownSettings.endDate = campaignEndMatch[1];
            }
          }
          foundSettings = true;
          // console.log('Loyalty sync: Updated countdown settings from script', this.countdownSettings);
        }
      }
    });

    if (!this.countdownSettings) {
      // console.log('Loyalty sync: No countdown settings found');
      return false;
    }
    
    return true;
  }

  isWithinCampaign() {
    // Always do date checking first to ensure we're in the right time period
    if (!this.countdownSettings?.enabled || !this.countdownSettings?.startDate || !this.countdownSettings?.endDate) {
      // console.log('Loyalty sync: No countdown settings, not in campaign');
      return false;
    }

    const now = new Date();
    const campaignStart = this.parseCopenhagenDate(this.countdownSettings.startDate);
    const campaignEnd = this.parseCopenhagenDate(this.countdownSettings.endDate);

    // console.log('Loyalty sync: Date check', {
    //   now: now.toISOString(),
    //   campaignStart: campaignStart?.toISOString(),
    //   campaignEnd: campaignEnd?.toISOString(),
    //   twoPhase: this.countdownSettings.twoPhaseEnabled
    // });

    // For two-phase countdown, only show discount during phase 2 (actual campaign)
    if (this.countdownSettings.twoPhaseEnabled) {
      // Discount is only active during the actual campaign phase (phase 2)
      const inPhase2 = now >= campaignStart && now < campaignEnd;
      // console.log('Loyalty sync: Two-phase campaign, in phase 2:', inPhase2);
      return inPhase2;
    }

    // For regular countdown, show discount only when campaign is active
    const inCampaign = now >= campaignStart && now < campaignEnd;
    // console.log('Loyalty sync: Regular campaign, active:', inCampaign);
    return inCampaign;
  }

  getCampaignPhase() {
    if (!this.countdownSettings?.enabled || !this.countdownSettings?.startDate || !this.countdownSettings?.endDate) {
      return 'inactive';
    }

    const now = new Date();
    const campaignStart = this.parseCopenhagenDate(this.countdownSettings.startDate);
    const campaignEnd = this.parseCopenhagenDate(this.countdownSettings.endDate);

    if (this.countdownSettings.twoPhaseEnabled) {
      const phase1Start = new Date(campaignStart.getTime() - (this.countdownSettings.phase1Hours * 60 * 60 * 1000));
      
      
      if (now < phase1Start) {
        return 'before';
      } else if (now >= phase1Start && now < campaignStart) {
        return 'phase1'; // Pre-launch phase
      } else if (now >= campaignStart && now < campaignEnd) {
        return 'phase2'; // Active campaign
      } else {
        return 'after';
      }
    } else {
      if (now < campaignStart) {
        return 'before';
      } else if (now >= campaignStart && now < campaignEnd) {
        return 'active';
      } else {
        return 'after';
      }
    }
  }

  parseCopenhagenDate(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') {
      return null;
    }
    
    try {
      // Parse the date string assuming Copenhagen timezone
      const [datePart, timePart] = dateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = (timePart || '00:00').split(':').map(Number);
      
      // Create date and adjust for Copenhagen timezone
      const date = new Date();
      date.setFullYear(year, month - 1, day);
      date.setHours(hour, minute, 0, 0);
      
      // Simple timezone adjustment (for production, use proper timezone library)
      const copenhagenOffset = this.getCopenhagenOffset(date);
      const localOffset = date.getTimezoneOffset();
      const offsetDiff = (copenhagenOffset - localOffset) * 60000;
      
      return new Date(date.getTime() - offsetDiff);
    } catch (error) {
      console.error('Error parsing Copenhagen date:', error);
      return null;
    }
  }

  getCopenhagenOffset(date) {
    // Copenhagen is CET (UTC+1) in winter and CEST (UTC+2) in summer
    const year = date.getFullYear();
    
    // DST typically starts last Sunday in March and ends last Sunday in October
    const dstStart = this.getLastSundayOfMonth(year, 2); // March (0-indexed)
    const dstEnd = this.getLastSundayOfMonth(year, 9);   // October (0-indexed)
    
    const isDST = date >= dstStart && date < dstEnd;
    return isDST ? -120 : -60;
  }

  getLastSundayOfMonth(year, month) {
    const lastDay = new Date(year, month + 1, 0);
    const lastSunday = new Date(lastDay);
    lastSunday.setDate(lastDay.getDate() - lastDay.getDay());
    lastSunday.setHours(2, 0, 0, 0);
    return lastSunday;
  }

  showLoyaltyDiscounts() {
    // Update all product price elements with loyalty discount
    const priceElements = document.querySelectorAll('[data-loyalty-sync="true"][data-loyalty-discount="true"]');
    
    priceElements.forEach(element => {
      element.classList.add('loyalty-discount-active');
      
      // The prices are already rendered server-side, just ensure they're visible
      const highlightPrice = element.querySelector('.Price--highlight');
      const comparePrice = element.querySelector('.Price--compareAt');
      const badge = element.querySelector('.Price--loyaltyBadge');
      const regularPrice = element.querySelector('.Price--regular');
      
      if (highlightPrice) highlightPrice.style.display = '';
      if (comparePrice) comparePrice.style.display = '';
      if (badge) badge.style.display = '';
      // Hide regular price when showing discount
      if (regularPrice) regularPrice.style.display = 'none';
    });
    
    // Also handle cart items
    const cartPriceElements = document.querySelectorAll('.CartItem__PriceList [data-loyalty-sync="true"][data-loyalty-discount="true"], .CartItem__LinePriceList [data-loyalty-sync="true"][data-loyalty-discount="true"]');
    cartPriceElements.forEach(element => {
      element.classList.add('loyalty-discount-active');
      const highlightPrice = element.querySelector('.Price--highlight');
      const comparePrice = element.querySelector('.Price--compareAt');
      const badge = element.querySelector('.Price--loyaltyBadge');
      const regularPrice = element.querySelector('.Price--regular');
      
      if (highlightPrice) highlightPrice.style.display = '';
      if (comparePrice) comparePrice.style.display = '';
      if (badge) badge.style.display = '';
      // Hide regular price when showing discount
      if (regularPrice) regularPrice.style.display = 'none';
    });
    
    // Also update cart total price
    this.updateCartTotalPrice(true);
  }

  hideLoyaltyDiscounts() {
    // Hide loyalty discount prices for synced elements
    // Find all elements with loyalty discount and sync enabled
    const elements = document.querySelectorAll('[data-loyalty-sync="true"][data-loyalty-discount="true"]');
    
    elements.forEach(element => {
      // Special handling for cart items
      if (element.closest('.CartItem__PriceList')) {
        this.hideCartItemDiscount(element);
      } else {
        this.hideDiscountForElement(element);
      }
    });
    
    // Also restore cart total price
    this.updateCartTotalPrice(false);
  }
  
  hideCartItemDiscount(element) {
    element.classList.remove('loyalty-discount-active');
    
    // For cart items, we need to handle the entire price container
    const container = element;
    
    // Remove all existing price elements
    container.innerHTML = '';
    
    // Create new price element with original price
    const originalPrice = container.dataset.originalPrice;
    if (originalPrice) {
      const priceSpan = document.createElement('span');
      priceSpan.className = 'CartItem__Price Price';
      priceSpan.textContent = this.formatMoneyFromCents(originalPrice);
      container.appendChild(priceSpan);
    }
  }
  
  hideDiscountForElement(element) {
    element.classList.remove('loyalty-discount-active');
    
    // For non-cart items, swap the prices
    const highlightPrice = element.querySelector('.Price--highlight');
    const comparePrice = element.querySelector('.Price--compareAt');
    
    if (highlightPrice && comparePrice) {
      // Swap the content - original price becomes the main price
      const originalPriceText = comparePrice.textContent;
      
      // Hide both discount prices
      highlightPrice.style.display = 'none';
      comparePrice.style.display = 'none';
      
      // Check if we already have a regular price element
      let regularPrice = element.querySelector('.Price--regular');
      if (!regularPrice) {
        // Create new regular price element
        regularPrice = document.createElement('span');
        const isCartItem = element.closest('.CartItem__PriceList, .CartItem__LinePriceList');
        regularPrice.className = isCartItem ? 'CartItem__Price Price Price--regular' : 'ProductItem__Price Price Price--regular';
        regularPrice.textContent = originalPriceText;
        element.insertBefore(regularPrice, element.firstChild);
      } else {
        // Show existing regular price
        regularPrice.style.display = '';
        regularPrice.textContent = originalPriceText;
      }
    }
    
    // Hide any badge
    const badge = element.querySelector('.Price--loyaltyBadge');
    if (badge) badge.style.display = 'none';
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your theme's money format
    const amount = (parseInt(cents) / 100).toFixed(2);
    return `${amount} kr`;
  }
  
  updateCartTotalPrice(showDiscount) {
    // Update cart total price in drawer - select all elements with cart total
    const cartTotalElements = document.querySelectorAll('.Cart__TotalPrice');
    
    cartTotalElements.forEach(totalElement => {
      // Check if this element has loyalty discount data
      if (totalElement.dataset.loyaltyDiscount === 'true' && totalElement.dataset.loyaltySync === 'true') {
        const originalTotal = totalElement.dataset.originalTotal;
        const discountTotal = totalElement.dataset.discountTotal;
        
        if (showDiscount && discountTotal && originalTotal !== discountTotal) {
          // Show discounted total
          const formattedPrice = this.formatMoneyFromCents(discountTotal);
          totalElement.textContent = formattedPrice;
          totalElement.classList.add('loyalty-discount-active');
        } else if (originalTotal) {
          // Show original total
          const formattedPrice = this.formatMoneyFromCents(originalTotal);
          totalElement.textContent = formattedPrice;
          totalElement.classList.remove('loyalty-discount-active');
        }
      }
    });
  }
  
  parseMoneyText(moneyText) {
    // Parse money text like "1.234 kr" to cents
    const cleanText = moneyText.replace(/[^\d,.-]/g, '').replace('.', '').replace(',', '.');
    return Math.round(parseFloat(cleanText) * 100);
  }
  
  formatMoneyFromCents(cents) {
    // Format cents to money string matching Shopify format
    const amount = cents / 100;
    const formatted = amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formatted} kr`;
  }
  
  updateAnnouncementBarVisibility(phase) {
    const announcementSection = document.querySelector('[data-section-type="announcement-bar"]');
    if (!announcementSection) return;
    
    const sectionElement = announcementSection.closest('section');
    if (!sectionElement) return;
    
    // Show announcement bar during phase1, phase2, or active campaign
    if (phase === 'phase1' || phase === 'phase2' || phase === 'active') {
      if (sectionElement.style.display === 'none') {
        sectionElement.style.display = '';
        // Update announcement bar height
        setTimeout(() => {
          const height = sectionElement.offsetHeight;
          document.documentElement.style.setProperty('--announcement-bar-height', height + 'px');
        }, 50);
      }
    } else if (phase === 'before' || phase === 'after') {
      // Hide announcement bar before campaign starts or after it ends
      if (sectionElement.style.display !== 'none') {
        sectionElement.style.display = 'none';
        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      }
    }
  }

  startPeriodicChecking() {
    // Initialize campaign status
    this.lastCampaignStatus = null;
    this.checkAttempts = 0;
    this.maxCheckAttempts = 60; // Try for up to 5 minutes
    
    // Initial check immediately
    this.checkCampaignStatus();
    
    // Another check after a short delay
    setTimeout(() => {
      this.checkCampaignStatus();
    }, 500);
    
    // Set up periodic checking
    this.checkInterval = setInterval(() => {
      this.checkCampaignStatus();
    }, 5000); // Check every 5 seconds
  }
  
  checkCampaignStatus() {
    // Try to get settings if we don't have them yet
    if (!this.countdownSettings) {
      this.getCountdownSettings();
      this.checkAttempts++;
      
      if (this.checkAttempts > this.maxCheckAttempts) {
        // console.log('Loyalty sync: Stopping checks after', this.checkAttempts, 'attempts');
        clearInterval(this.checkInterval);
        return;
      }
    }
    
    // If we have settings, check campaign status
    if (this.countdownSettings) {
      const campaignPhase = this.getCampaignPhase();
      const isActive = this.isWithinCampaign();
      
      // console.log('Loyalty sync: Periodic check', {
      //   phase: campaignPhase,
      //   isActive: isActive,
      //   lastStatus: this.lastCampaignStatus,
      //   attempt: this.checkAttempts
      // });
      
      // Update announcement bar visibility based on campaign phase
      this.updateAnnouncementBarVisibility(campaignPhase);
      
      // Update display based on current status
      if (this.lastCampaignStatus === null || isActive !== this.lastCampaignStatus) {
        // console.log('Loyalty sync: Campaign status changed from', this.lastCampaignStatus, 'to', isActive);
        
        if (isActive) {
          // console.log('Loyalty sync: Campaign is now active - showing discounts');
          this.showLoyaltyDiscounts();
          
          // Trigger auto-apply if campaign is active and discount not yet applied
          // This handles both: campaign just started OR customer arrived mid-campaign
          if (window.loyaltyDiscountAutoApply) {
            // Check if discount hasn't been applied yet
            const sessionApplied = sessionStorage.getItem('nordal_loyalty_discount_session');
            const isRedirecting = sessionStorage.getItem('nordal_loyalty_discount_redirecting');
            const syncTriggered = sessionStorage.getItem('nordal_loyalty_discount_triggered_by_sync');
            
            if (!sessionApplied && !isRedirecting && !syncTriggered) {
              // console.log('Loyalty sync: Campaign is active - triggering auto-apply');
              // Mark that we've triggered it to prevent repeated calls
              sessionStorage.setItem('nordal_loyalty_discount_triggered_by_sync', 'true');
              
              // Wait a bit to ensure DOM is ready
              setTimeout(() => {
                window.loyaltyDiscountAutoApply.applyDiscount();
              }, 1000);
            }
          }
        } else {
          // console.log('Loyalty sync: Campaign is not active - hiding discounts');
          this.hideLoyaltyDiscounts();
        }
        
        this.lastCampaignStatus = isActive;
      }
      
      // If campaign has ended, stop checking
      if (campaignPhase === 'after') {
        // console.log('Loyalty sync: Campaign has ended, stopping periodic checks');
        clearInterval(this.checkInterval);
      }
    }
  }
  
  handleCountdownExpired(event) {
    if (this.settings.loyalty_sync_with_countdown) {
      this.hideLoyaltyDiscounts();
      // Stop periodic checking
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }
    }
  }
  
  listenForCountdownEvents() {
    // Listen for countdown expiration events
    document.addEventListener('countdownExpired', this.boundCountdownHandler);
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      if (this.isWithinCampaign()) {
        // Re-apply discount display after cart update
        setTimeout(() => {
          this.showLoyaltyDiscounts();
        }, 100);
      }
    });
  }
  
  observeCartDrawer() {
    // Create observer for cart drawer DOM changes
    this.cartObserver = new MutationObserver((mutations) => {
      // Check if cart drawer content has been updated
      let cartUpdated = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && 
            (mutation.target.classList.contains('Cart__ItemList') || 
             mutation.target.classList.contains('Drawer__Main') ||
             mutation.target.classList.contains('Drawer__Container'))) {
          cartUpdated = true;
        }
      });
      
      if (cartUpdated) {
        // Always update based on current campaign status
        setTimeout(() => {
          const isActive = this.isWithinCampaign();
          if (isActive) {
            this.showLoyaltyDiscounts();
          } else {
            this.hideLoyaltyDiscounts();
          }
        }, 100);
      }
    });
    
    // Start observing cart drawer
    const cartDrawer = document.querySelector('#sidebar-cart');
    if (cartDrawer) {
      this.cartObserver.observe(cartDrawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }
    
    // Also listen for cart drawer open/close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]') ||
          e.target.closest('[aria-controls="sidebar-cart"]')) {
        setTimeout(() => {
          const isActive = this.isWithinCampaign();
          if (isActive) {
            this.showLoyaltyDiscounts();
          } else {
            this.hideLoyaltyDiscounts();
          }
        }, 300);
      }
    });
  }
  
  destroy() {
    // Clear interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    // Remove event listener
    document.removeEventListener('countdownExpired', this.boundCountdownHandler);
    
    // Disconnect observer
    if (this.cartObserver) {
      this.cartObserver.disconnect();
      this.cartObserver = null;
    }
    
    // Clear cached elements
    this.priceElements = null;
    this.cartPriceElements = null;
    
    // Reset status
    this.lastCampaignStatus = null;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Add CSS to prevent initial flicker - but not in cart drawer
  const style = document.createElement('style');
  style.textContent = `
    /* Only apply opacity rules to product grids and product pages, not cart */
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--highlight,
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--compareAt,
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--loyaltyBadge,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--highlight,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--compareAt,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"] .Price--loyaltyBadge {
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--highlight,
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--compareAt,
    .ProductItem__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--loyaltyBadge,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--highlight,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--compareAt,
    .ProductMeta__PriceList[data-loyalty-sync="true"][data-loyalty-discount="true"].loyalty-discount-active .Price--loyaltyBadge {
      opacity: 1;
    }
    
    /* Cart items should always be visible */
    .CartItem__PriceList .Price,
    .CartItem__LinePriceList .Price {
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
  
  window.loyaltyCountdownSync = new LoyaltyCountdownSync();
});

// Also initialize on theme editor events
document.addEventListener('shopify:section:load', () => {
  window.loyaltyCountdownSync = new LoyaltyCountdownSync();
});

document.addEventListener('shopify:section:reorder', () => {
  window.loyaltyCountdownSync = new LoyaltyCountdownSync();
});